import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Power3, gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import ScrollContainer from './common/ScrollContainer';
import StartScreen, { StartScreenContainer } from './common/StartScreen';
import { defaultProps, propTypes } from '../ModelDetail/propsFile';
import { ID_TEXTS } from '../../utils/constants';
import {
  createFadeOutColorLine,
  createImageRotationTimeline,
  createShowFeatureTimeline,
  createStartScreenTimeline,
  createSlideUpFeatureTimeline,
} from './common/timeline';
import Feature, {
  FeatureStack,
  FeatureStackCentered,
  FeatureContainer,
  FeatureItem,
  FeatureBottom,
} from './common/Feature';
import Image, {
  ColorLine,
  ImageBaseContainer,
  ImageContainer,
  ImageController,
  ImageWrapper,
} from './common/Image';
import { DEFAULT_360_IMAGE_SIZE } from '../../utils/image';

const TOTAL_360_IMGS = 36;
const PRELOADING_THRESHOLD = Math.ceil(TOTAL_360_IMGS / 8);

const imageUrls = Array.from({ length: TOTAL_360_IMGS }).map(
  (_, index) =>
    `https://app.irobot.mdsrv.media/${DEFAULT_360_IMAGE_SIZE}/public-assets/product-demo-v2/intros/rc2-essential-white/Roomba_Combo_2_Essential_White_360Images_${
      (index + 1) * 10
    }.png`
);

const imageBaseUrl =
  'https://app.irobot.mdsrv.media/cdn-cgi/image/width=1280/public-assets/product-demo-v2/intros/rc2-essential-white/Roomba_Combo_2_Essential_White_Photo_Studio_AutoEmpty_LeftFacing.png';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

export default function TemplateOPPWhiteAutoEvac({
  bgColor,
  logo,
  logoAlt,
  modelName,
  subtitleFont,
  subtitleColor,
  title,
  titleFont,
  titleColor,
  accentColor,
  featuresTitleColor,
  featuresModelNameColor,
  featuresDescriptionColor,
  features,
}) {
  const [containerHeight, setContainerHeight] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);

  const [loading, setLoading] = useState(true);
  const [loadedImages, setLoadedImages] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(1);

  const scrollContainerRef = useRef(null);
  const mainTimeLineRef = useRef(null);
  const scrollTriggerRef = useRef(null);
  const scrollToRef = useRef(null);

  function handleImageLoad(e) {
    setImageHeight(e.target.clientHeight);
    setLoadedImages(counter => counter + 1);
  }

  function handleNext(label, duration = 3) {
    const mainTimeline = mainTimeLineRef.current;
    const scrollTrigger = scrollTriggerRef.current;
    if (mainTimeline && scrollTrigger) {
      const targetLabel = mainTimeline.labels[label];
      if (targetLabel) {
        const percent = targetLabel / mainTimeline.totalDuration();
        const scrollPos =
          scrollTrigger.start +
          (scrollTrigger.end - scrollTrigger.start) * percent;
        if (scrollToRef.current) {
          scrollToRef.current.kill();
        }
        scrollToRef.current = gsap.to(window, {
          duration,
          scrollTo: scrollPos,
          onComplete: () => {
            scrollToRef.current = null;
          },
        });
      }
    }
  }

  function handleEnd() {
    if (scrollToRef.current) {
      scrollToRef.current.kill();
    }
    scrollToRef.current = gsap.to(window, {
      duration: 1,
      scrollTo: `#${ID_TEXTS.MAIN_CONTENT_REF}`,
      onComplete: () => {
        scrollToRef.current = null;
      },
    });
  }

  useEffect(() => {
    function handleWrapperChange() {
      setContainerHeight(document.documentElement.clientHeight);
    }
    handleWrapperChange();

    window.addEventListener('resize', handleWrapperChange);
    window.addEventListener('deviceorientation', handleWrapperChange);

    return () => {
      window.removeEventListener('resize', handleWrapperChange);
      window.removeEventListener('deviceorientation', handleWrapperChange);
    };
  }, []);

  useEffect(() => {
    if (loadedImages >= PRELOADING_THRESHOLD) {
      setLoading(false);
    }
  }, [loadedImages]);

  useEffect(() => {
    const element = scrollContainerRef.current;
    if (!loading && element && imageHeight > 0) {
      const startScreenTimeline = createStartScreenTimeline({ element });
      const createFeatureOneTimeline = () => {
        const fadeOutColorLine = createFadeOutColorLine({ element });
        const showFeature = createShowFeatureTimeline({
          element,
          nextFeature: `#${ID_TEXTS.FEATURE_1}`,
        });
        const rotateImage = createImageRotationTimeline({
          element,
          startFrame: 36,
          endFrame: 19,
          attributes: {
            y: containerHeight / 2 - imageHeight * 1.25,
            x: 180,
            scale: 1.75,
            transformOrigin: 'center top',
          },
          onUpdate: index => setCurrentImageIndex(index),
        });
        return gsap
          .timeline()
          .add(fadeOutColorLine)
          .add(showFeature, 0)
          .add(rotateImage, 0);
      };
      const createFeatureTwoTimeline = () => {
        const fadeOutColorLine = createFadeOutColorLine({ element });
        const showFeature = createShowFeatureTimeline({
          element,
          prevFeature: `#${ID_TEXTS.FEATURE_1}`,
          nextFeature: `#${ID_TEXTS.FEATURE_2}`,
        });
        const rotateImage = createImageRotationTimeline({
          element,
          startFrame: 19,
          endFrame: 1,
          onUpdate: index => setCurrentImageIndex(index),
        });
        const translateImage = gsap
          .timeline()
          .to(element.querySelector(`#${ID_TEXTS.IMAGES_CONTAINER}`), {
            y: -containerHeight / 2,
            x: 0,
            opacity: 0,
            ease: Power3.easeOut,
          });
        const slideUpImageBase = gsap
          .timeline()
          .to(element.querySelector(`#${ID_TEXTS.IMAGE_BASE_CONTAINER}`), {
            top: 0,
            y: '-7.5%',
            x: 100,
            scale: 1.2,
            transformOrigin: 'center top',
          });
        return gsap
          .timeline()
          .add(fadeOutColorLine)
          .add(showFeature, 0)
          .add(rotateImage, 0.1)
          .add(translateImage, 0.16)
          .add(slideUpImageBase, 0.2);
      };
      const createFeatureFourTimeline = () => {
        const hideFeature = createSlideUpFeatureTimeline({
          element,
          feature: `#${ID_TEXTS.FEATURE_2}`,
          containerHeight,
        });
        const translateImageBase = gsap
          .timeline()
          .to(element.querySelector(`#${ID_TEXTS.IMAGE_BASE_CONTAINER}`), {
            y: '-100%',
            x: 100,
            scale: 1,
            opacity: 0,
            transformOrigin: 'center top',
            ease: Power3.easeOut,
          });
        const imageBase = gsap
          .timeline()
          .add(translateImageBase)
          .add(hideFeature, 0);
        const showFeature = createShowFeatureTimeline({
          element,
          nextFeature: `#${ID_TEXTS.FEATURE_3}`,
        });
        const rotateImage = createImageRotationTimeline({
          element,
          startFrame: 1,
          endFrame: 19,
          attributes: {
            y: -imageHeight / 3,
            transformOrigin: 'center top',
          },
          onUpdate: index => setCurrentImageIndex(index),
        });
        const translateImage = gsap
          .timeline()
          .to(element.querySelector(`#${ID_TEXTS.IMAGES_CONTAINER}`), {
            x: 200,
            scale: 2,
            transformOrigin: 'center top',
          });
        const image = gsap
          .timeline()
          .set(element.querySelector(`#${ID_TEXTS.IMAGES_CONTAINER}`), {
            y: containerHeight,
            opacity: 1,
            zIndex: 2,
          })
          .set(element.querySelector(`#${ID_TEXTS.COLOR_LINE}`), {
            opacity: 1,
            zIndex: 1,
            scale: 0.8,
            top: 0,
          })
          .add(rotateImage, 0.2)
          .add(showFeature, 0.3)
          .add(translateImage, 0.45)
          .to(
            element.querySelector(`#${ID_TEXTS.COLOR_LINE}`),
            {
              opacity: 0,
              duration: 0.1,
            },
            0.35
          );
        return gsap.timeline().add(imageBase).add(image, 0.1);
      };
      const createFeatureFiveTimeline = () => {
        const showFeature = createShowFeatureTimeline({
          element,
          prevFeature: `#${ID_TEXTS.FEATURE_3}`,
          nextFeature: `#${ID_TEXTS.FEATURE_4}`,
        });
        const rotateImage = createImageRotationTimeline({
          element,
          startFrame: 19,
          endFrame: 28,
          attributes: {
            x: '30%',
            y: -containerHeight / 4,
            scale: (containerHeight / imageHeight) * 1.4,
            transformOrigin: 'center top',
            ease: Power3.easeInOut,
          },
          onUpdate: index => setCurrentImageIndex(index),
        });
        return gsap
          .timeline()
          .add(showFeature, 0)
          .add(rotateImage, 0)
          .to(
            element.querySelector(`#${ID_TEXTS.IMAGES_CONTAINER}`),
            {
              opacity: 0,
              duration: 0.6,
              y: -containerHeight / 2,
              ease: Power3.easeIn,
            },
            0.8
          );
      };

      const mainTimeLine = gsap.timeline();
      mainTimeLine.addLabel('step1');
      mainTimeLine.add(startScreenTimeline, '+=0.5');
      mainTimeLine.addLabel('step2');
      mainTimeLine.add(createFeatureOneTimeline(), '+=0.5');
      mainTimeLine.addLabel('step3');
      mainTimeLine.add(createFeatureTwoTimeline(), '+=0.5');
      mainTimeLine.addLabel('step4');
      mainTimeLine.add(createFeatureFourTimeline(), '+=0.5');
      mainTimeLine.addLabel('step5');
      mainTimeLine.add(createFeatureFiveTimeline(), '+=0.5');
      mainTimeLine.addLabel('step8');

      const scrollTrigger = ScrollTrigger.create({
        animation: mainTimeLine,
        trigger: element,
        start: 'top top',
        end: '+=700%',
        scrub: true,
        pin: true,
      });

      mainTimeLineRef.current = mainTimeLine;
      scrollTriggerRef.current = scrollTrigger;
    }
  }, [containerHeight, imageHeight, loading]);

  return (
    <ScrollContainer bgColor={bgColor} ref={scrollContainerRef}>
      {loading || containerHeight === 0 ? <Loader /> : null}
      {containerHeight > 0 ? (
        <StartScreenContainer
          id={ID_TEXTS.INTRO_CONTENT}
          height={Math.ceil(containerHeight * 0.7)}
        >
          <StartScreen
            logo={logo}
            logoAlt={logoAlt}
            model={modelName}
            modelFont={subtitleFont}
            modelColor={subtitleColor}
            title={title}
            titleFont={titleFont}
            titleColor={titleColor}
            accentColor={accentColor}
            height={Math.ceil(containerHeight * 0.7)}
            overlay
            onNext={() => handleNext('step3', 2)}
          />
        </StartScreenContainer>
      ) : null}
      <ImageContainer id={ID_TEXTS.IMAGES_CONTAINER} height={containerHeight}>
        <ImageController height={Math.ceil(containerHeight * 0.3 * 2)}>
          {imageUrls.map((src, index) => (
            <ImageWrapper key={`opp-white-auto-evac-image-${index + 1}`}>
              <Image
                src={src}
                alt={`opp-white-auto-evac-360-image frame ${index + 1}`}
                active={index + 1 === currentImageIndex}
                onLoad={handleImageLoad}
              />
            </ImageWrapper>
          ))}
          <ColorLine
            id={ID_TEXTS.COLOR_LINE}
            color={accentColor}
            marginTop={imageHeight / 2}
          />
        </ImageController>
      </ImageContainer>
      <ImageBaseContainer
        id={ID_TEXTS.IMAGE_BASE_CONTAINER}
        height={containerHeight}
      >
        <ImageController height={Math.ceil(containerHeight * 0.3 * 2)}>
          <ImageWrapper>
            <Image
              src={imageBaseUrl}
              alt="OPP White Combo with Auto-EVAC base"
              active
            />
          </ImageWrapper>
        </ImageController>
      </ImageBaseContainer>
      <FeatureContainer>
        <FeatureStack id={ID_TEXTS.FEATURE_1}>
          <FeatureItem />
          <FeatureItem>
            <FeatureBottom
              icon={features[0]?.icon_url}
              iconAlt={features[0]?.icon_alt_txt}
              model={features[0]?.subtitle}
              modelColor={featuresModelNameColor}
              modelFont={subtitleFont}
              title={features[0]?.title}
              titleColor={featuresTitleColor}
              titleFont={titleFont}
              description={features[0]?.description}
              descriptionColor={featuresDescriptionColor}
              accentColor={accentColor}
              footnote={features[0]?.legal_footnote}
              overlay
              onNext={() => handleNext('step4', 1)}
            />
          </FeatureItem>
        </FeatureStack>
        <FeatureStack id={ID_TEXTS.FEATURE_2}>
          <FeatureItem />
          <FeatureBottom
            icon={features[1]?.icon_url}
            iconAlt={features[1]?.icon_alt_txt}
            model={features[1]?.subtitle}
            modelColor={featuresModelNameColor}
            modelFont={subtitleFont}
            title={features[1]?.title}
            titleColor={featuresTitleColor}
            titleFont={titleFont}
            description={features[1]?.description}
            descriptionColor={featuresDescriptionColor}
            accentColor={accentColor}
            footnote={features[1]?.legal_footnote}
            overlay
            onNext={() => handleNext('step5', 3)}
          />
        </FeatureStack>
        <FeatureStack id={ID_TEXTS.FEATURE_3}>
          <FeatureItem />
          <FeatureBottom
            icon={features[2]?.icon_url}
            iconAlt={features[2]?.icon_alt_txt}
            model={features[2]?.subtitle}
            modelColor={featuresModelNameColor}
            modelFont={subtitleFont}
            title={features[2]?.title}
            titleColor={featuresTitleColor}
            titleFont={titleFont}
            description={features[2]?.description}
            descriptionColor={featuresDescriptionColor}
            accentColor={accentColor}
            footnote={features[2]?.legal_footnote}
            overlay
            onNext={() => handleNext('step6', 3)}
          />
        </FeatureStack>
        <FeatureStackCentered id={ID_TEXTS.FEATURE_4}>
          <Feature
            icon={features[3]?.icon_url}
            iconAlt={features[3]?.icon_alt_txt}
            model={features[3]?.subtitle}
            modelColor={featuresModelNameColor}
            modelFont={subtitleFont}
            title={features[3]?.title}
            titleColor={featuresTitleColor}
            titleFont={titleFont}
            description={features[3]?.description}
            descriptionColor={featuresDescriptionColor}
            accentColor={accentColor}
            footnote={features[3]?.legal_footnote}
            overlay
            onNext={() => handleEnd}
          />
        </FeatureStackCentered>
      </FeatureContainer>
    </ScrollContainer>
  );
}

TemplateOPPWhiteAutoEvac.propTypes = propTypes;
TemplateOPPWhiteAutoEvac.defaultProps = defaultProps;

const Loader = styled.div``;
